import { CAPTURE_FULL, coordKey, unitAt, type PlayerId } from "@dailywars/core/engine";
import type { GameState } from "@dailywars/core/engine";

import type { CaptureBarHoverPreview } from "#/game/captureBarHoverPreview";

import { CAPTURE_BAR_TRACK_FILL, tileIndex, TILE_SIZE } from "./boardConstants";

const CAPTURE_BAR_COLS = 2;
const CAPTURE_BAR_ROWS = 10;
const CAPTURE_BAR_CELL = 3;
const CAPTURE_BAR_GAP = 1;
const CAPTURE_BAR_INSET = 3;
const CAPTURE_BAR_OUTLINE = 2;
const CAPTURE_BAR_OUTLINE_FILL = "#000000";
const CAPTURE_BAR_IDLE_FILL = "#888888";
const CAPTURE_BAR_FILL: Record<PlayerId, string> = {
  p1: "#68aafd",
  p2: "#ff6464",
};
const CAPTURE_BAR_PREVIEW_FILL: Record<PlayerId, string> = {
  p1: "#b8dcff",
  p2: "#ffb0b0",
};

const CAPTURE_BAR_WIDTH =
  CAPTURE_BAR_COLS * CAPTURE_BAR_CELL + (CAPTURE_BAR_COLS - 1) * CAPTURE_BAR_GAP;
const CAPTURE_BAR_HEIGHT =
  CAPTURE_BAR_ROWS * CAPTURE_BAR_CELL + (CAPTURE_BAR_ROWS - 1) * CAPTURE_BAR_GAP;

function drawCaptureBar({
  ctx,
  tileX,
  tileY,
  filledCount,
  previewFilledCount,
  barColor,
  previewBarColor,
}: {
  ctx: CanvasRenderingContext2D;
  tileX: number;
  tileY: number;
  filledCount: number;
  previewFilledCount?: number;
  barColor: string;
  previewBarColor?: string;
}): void {
  const barX = tileX + CAPTURE_BAR_INSET;
  const barY = tileY + Math.floor((TILE_SIZE - CAPTURE_BAR_HEIGHT) / 2);

  ctx.fillStyle = CAPTURE_BAR_OUTLINE_FILL;
  ctx.fillRect(
    barX - CAPTURE_BAR_OUTLINE,
    barY - CAPTURE_BAR_OUTLINE,
    CAPTURE_BAR_WIDTH + CAPTURE_BAR_OUTLINE * 2,
    CAPTURE_BAR_HEIGHT + CAPTURE_BAR_OUTLINE * 2,
  );

  for (let i = 0; i < CAPTURE_FULL; i++) {
    const col = i % CAPTURE_BAR_COLS;
    const rowFromBottom = Math.floor(i / CAPTURE_BAR_COLS);
    const rowFromTop = CAPTURE_BAR_ROWS - 1 - rowFromBottom;
    const cellX = barX + col * (CAPTURE_BAR_CELL + CAPTURE_BAR_GAP);
    const cellY = barY + rowFromTop * (CAPTURE_BAR_CELL + CAPTURE_BAR_GAP);
    const fill =
      i < filledCount
        ? barColor
        : previewFilledCount !== undefined &&
            previewBarColor !== undefined &&
            i < previewFilledCount
          ? previewBarColor
          : CAPTURE_BAR_TRACK_FILL;
    ctx.fillStyle = fill;
    ctx.fillRect(cellX, cellY, CAPTURE_BAR_CELL, CAPTURE_BAR_CELL);
  }
}

export function drawCaptureBarsLayer({
  ctx,
  state,
  hiddenTileKeys = new Set<string>(),
  hoverPreview = null,
}: {
  ctx: CanvasRenderingContext2D;
  state: GameState;
  hiddenTileKeys?: Set<string>;
  hoverPreview?: CaptureBarHoverPreview | null;
}): void {
  for (let y = 0; y < state.height; y++) {
    for (let x = 0; x < state.width; x++) {
      const pos = { x, y };
      if (hiddenTileKeys.has(coordKey(pos))) continue;

      const tile = state.tiles[tileIndex({ x, y, width: state.width })];
      const capturePoints = tile.capturePoints;
      if (capturePoints === undefined || capturePoints === CAPTURE_FULL) continue;

      const tileKey = coordKey(pos);
      const capturingUnit = unitAt(state, { x, y });
      const barColor = capturingUnit
        ? CAPTURE_BAR_FILL[capturingUnit.owner]
        : CAPTURE_BAR_IDLE_FILL;
      const preview =
        hoverPreview?.tileKey === tileKey
          ? {
              filledCount: hoverPreview.filledCount,
              previewFilledCount: hoverPreview.previewFilledCount,
              previewBarColor: CAPTURE_BAR_PREVIEW_FILL[hoverPreview.owner],
            }
          : null;
      drawCaptureBar({
        ctx,
        tileX: x * TILE_SIZE,
        tileY: y * TILE_SIZE,
        filledCount: preview?.filledCount ?? CAPTURE_FULL - capturePoints,
        previewFilledCount: preview?.previewFilledCount,
        barColor,
        previewBarColor: preview?.previewBarColor,
      });
    }
  }
}
